// @ts-nocheck
import React from 'react';
import { Collapse, Button, Spin } from 'antd';
import { MdEdit } from 'react-icons/md';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { IWindowCartItem } from '@/features/assistant/types';
import { getConfigurationsBreakdown, getDisplayValue } from '@/features/assistant/utils';
import { OPTION_TYPE_COUPLING_CLIP, OPTION_TYPE_EGRESS_COMPLAINT } from '@/features/assistant/constants';
import { FireEgressBadge } from '@/features/assistant/components/FireEgressBadge';
import { resolveFireEgressBadge } from '@/features/assistant/utils/fireEgress';

const { Panel } = Collapse;

interface QuoteDetailsWindowListProps {
    windowConfigurations: IWindowCartItem[];
    onEditConfiguration?: (itemId: string, item: IWindowCartItem, questionId: string) => void;
    onUpdateQuantity?: (itemId: string, quantity: number) => void;
    onDelete?: (itemId: string) => void;
    onAddWindow?: () => void;
    disableAllActions?: boolean;
    isLoadingConfigurations?: boolean;
}

export const QuoteDetailsWindowList: React.FC<QuoteDetailsWindowListProps> = ({
    windowConfigurations,
    onEditConfiguration,
    onUpdateQuantity,
    onDelete,
    onAddWindow,
    disableAllActions = false,
    isLoadingConfigurations = false,
}) => {
    const handleQuantityChange = (id: string, delta: number) => {
        const config = windowConfigurations.find(c => c.id === id);
        if (!config) return;
        const newQuantity = (config.quantity ?? 1) + delta;
        if (newQuantity < 1) return;
        onUpdateQuantity?.(id, newQuantity);
    };

    return (
        <div className="bg-white rounded-lg p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-semibold text-gray-900">
                    Window Configuration
                </h3>
                {onAddWindow && (
                    <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        disabled={disableAllActions}
                        onClick={onAddWindow}
                    >
                        Add Window
                    </Button>
                )}
            </div>

            {windowConfigurations.length > 0 ? (
                <Collapse
                    defaultActiveKey={[windowConfigurations[0]?.id]}
                    expandIconPosition="end"
                    className="bg-transparent border-none"
                    rootClassName='[&_.ant-collapse-body]:px-0'
                >
                    {windowConfigurations.map((config) => {
                        // Hide the raw egress Yes/No question — shown separately as the
                        // computed Compliant/Non-Compliant badge above, which reads worse
                        // alongside the raw answer (e.g. badge "Non-Compliant" next to
                        // answer line "Yes").
                        const configBreakdown = getConfigurationsBreakdown(config.messages)
                            .map(category => ({
                                ...category,
                                details: category.details.filter(
                                    detail => detail.message.question.optionType !== OPTION_TYPE_EGRESS_COMPLAINT
                                ),
                            }))
                            .filter(category => category.details.length > 0);
                        return (
                            <Panel
                                header={
                                    <div className="flex items-center justify-between">
                                        <div className="flex flex-col mr-2 min-w-0">
                                            <span className="font-medium text-gray-900 truncate">{config.windowName}</span>
                                            <span className="text-xs text-gray-500">QTY: {config.quantity ?? 1}</span>
                                        </div>
                                        <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                            {onDelete && (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    disabled={disableAllActions || windowConfigurations.length <= 1}
                                                    onClick={() => onDelete(config.id)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                }
                                key={config.id}
                                className="mb-4 bg-gray-50 rounded-lg"
                            >
                                {/* Configuration Details */}
                                <div className="space-y-4 text-sm bg-white rounded-md">
                                    {(() => {
                                        const egressStatus = resolveFireEgressBadge(config.messages);
                                        return egressStatus ? (
                                            <FireEgressBadge status={egressStatus} />
                                        ) : null;
                                    })()}
                                    {configBreakdown.length > 0 ? (
                                        configBreakdown
                                            .filter(category =>
                                                category.categoryName !== 'Surcharge' &&
                                                category.categoryName !== 'Additional Charges'
                                            )
                                            .map((category, categoryIndex) => (
                                                <div key={categoryIndex} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                                                    {category.categoryName && (
                                                        <div className="font-bold text-gray-900 mb-3 text-sm">
                                                            {category.categoryName}
                                                        </div>
                                                    )}
                                                    <div className="ml-4 space-y-2">
                                                        {(() => {
                                                            // Group details with the same optionTypeName into one row,
                                                            // BUT keep window-specific questions (e.g. "Sash(window 1)")
                                                            // as separate rows by including windowName in the key.
                                                            const seen = new Map<string, typeof category.details>();
                                                            category.details.forEach((detail) => {
                                                                const optionKey = detail.message.question.optionTypeName || detail.message.question.questionId;
                                                                const key = [
                                                                    optionKey,
                                                                    detail.message.question.questionType ?? '',
                                                                    detail.windowName ?? '',
                                                                ].join('__');
                                                                if (!seen.has(key)) seen.set(key, []);
                                                                seen.get(key)!.push(detail);
                                                            });

                                                            return Array.from(seen.entries()).map(([key, detailGroup]) => {
                                                                const firstDetail = detailGroup[0];
                                                                const label = firstDetail.windowName
                                                                    ? `${firstDetail.windowName} - ${firstDetail.message.question.optionTypeName}`
                                                                    : firstDetail.message.question.optionTypeName;

                                                                const displayValue = detailGroup.length === 1
                                                                    ? getDisplayValue(firstDetail.message)
                                                                    : detailGroup.map(d => getDisplayValue(d.message)).filter(Boolean).join(', ');

                                                                return (
                                                                    <div key={key} className="group hover:bg-gray-50 px-2 py-1 rounded transition-colors">
                                                                        <div className="flex justify-between items-start gap-4">
                                                                            <span className="text-gray-700 text-xs font-medium flex-shrink-0">
                                                                                {label}:
                                                                            </span>
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="text-gray-900 text-xs text-right font-normal">
                                                                                    {displayValue}
                                                                                </span>
                                                                                {onEditConfiguration && firstDetail.message.question?.questionId && firstDetail.message.question?.optionType !== OPTION_TYPE_COUPLING_CLIP && (
                                                                                    <MdEdit
                                                                                        className="text-primary-500 cursor-pointer hover:text-primary-600 transition-opacity flex-shrink-0"
                                                                                        size={12}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            onEditConfiguration(
                                                                                                config.id,
                                                                                                config,
                                                                                                firstDetail.message.question.questionId
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                </div>
                                            ))
                                    ) : isLoadingConfigurations ? (
                                        <div className="flex justify-center py-4">
                                            <Spin size="small" />
                                        </div>
                                    ) : (
                                        <div className="text-center text-gray-500 py-4">
                                            No configuration details available
                                        </div>
                                    )}
                                </div>

                                {/* Quantity Controls */}
                                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                                    <div className="flex items-center gap-2">
                                        {onUpdateQuantity ? (
                                            <>
                                                <Button
                                                    type="default"
                                                    size="small"
                                                    disabled={disableAllActions || (config.quantity ?? 1) <= 1}
                                                    onClick={() => handleQuantityChange(config.id, -1)}
                                                    className="!w-7 !h-7 !p-0 !min-w-0"
                                                >−</Button>
                                                <span className="text-sm text-gray-700 w-16 text-center font-medium">QTY: {config.quantity ?? 1}</span>
                                                <Button
                                                    type="default"
                                                    size="small"
                                                    disabled={disableAllActions}
                                                    onClick={() => handleQuantityChange(config.id, 1)}
                                                    className="!w-7 !h-7 !p-0 !min-w-0"
                                                >+</Button>
                                            </>
                                        ) : (
                                            <span className="text-sm text-gray-600">QTY: {config.quantity ?? 1}</span>
                                        )}
                                    </div>
                                </div>
                            </Panel>
                        );
                    })}
                </Collapse>
            ) : (
                <div className="text-center text-gray-500 py-8">
                    No window configurations added yet
                </div>
            )}
        </div>
    );
};
